import { useEffect, useRef, useState } from "react";
import { Heart, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { storefrontApiRequest, STOREFRONT_QUERY, shopifyImage, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { useFavoritesStore } from "@/stores/favoritesStore";
import { toast } from "sonner";
import { useCurrencyStore, formatConvertedPrice } from "@/stores/currencyStore";
import { NotifyMeDialog } from "@/components/NotifyMeDialog";

interface ProductGridProps {
  query?: string | null;
  first?: number;
  layout?: "grid" | "carousel";
  columns?: number;
  soldOut?: boolean;
  paginate?: boolean;
  pageSize?: number;
  showSizes?: boolean;
  hideWhenEmpty?: boolean;
  onEmptyChange?: (empty: boolean) => void;
}

export const ProductGrid = ({ query = null, first = 12, layout = "grid", columns = 4, soldOut = false, paginate = false, pageSize = 8, showSizes = false, hideWhenEmpty = false, onEmptyChange }: ProductGridProps) => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const favoriteIds = useFavoritesStore(s => s.ids);
  const toggleFavorite = useFavoritesStore(s => s.toggle);
  const displayCurrency = useCurrencyStore(s => s.code);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    if (layout !== "carousel") return;
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [layout, products.length]);

  const scrollByDir = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    storefrontApiRequest(STOREFRONT_QUERY, { first, query })
      .then(data => {
        const edges = data?.data?.products?.edges || [];
        setProducts(edges);
        onEmptyChange?.(edges.length === 0);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [query, first]);

  const handleAdd = async (product: ShopifyProduct) => {
    const selectedId = selectedVariants[product.node.id];
    const variant = (selectedId
      ? product.node.variants.edges.find(v => v.node.id === selectedId)?.node
      : undefined) || product.node.variants.edges[0]?.node;
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success("Added to cart", { position: "top-center" });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (products.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className="text-center py-20 border border-dashed border-border">
        <p className="font-display text-2xl mb-2">No products found</p>
        <p className="text-muted-foreground text-sm">Tell the chat what you'd like to sell — e.g. "Add a Brim Mob fitted cap for $48".</p>
      </div>
    );
  }

  const renderCard = (p: ShopifyProduct, index = 0) => {
        const img = p.node.images.edges[0]?.node;
        const price = p.node.priceRange.minVariantPrice;
        const isFav = favoriteIds.includes(p.node.id);
        const eager = index < columns;
        const sizeOption = p.node.options?.find(o => o.name.toLowerCase() === "size");
        const sizeVariants = sizeOption
          ? p.node.variants.edges.filter(v => v.node.selectedOptions.some(o => o.name.toLowerCase() === "size"))
          : [];
        const selectedVariantId = selectedVariants[p.node.id] || sizeVariants[0]?.node.id;
        return (
          <article key={p.node.id} className={`group card-lift flex flex-col h-full ${soldOut ? "select-none" : ""}`}>
            {soldOut ? (
              <div className="flex flex-col flex-1 cursor-not-allowed">
                <div className="relative bg-muted aspect-square overflow-hidden mb-4">
                  <span className="absolute top-4 left-4 font-display text-xs text-muted-foreground z-10">BRIM MOB</span>
                  {img ? (
                    <img
                      src={shopifyImage(img.url, 600)}
                      srcSet={`${shopifyImage(img.url, 400)} 400w, ${shopifyImage(img.url, 600)} 600w, ${shopifyImage(img.url, 900)} 900w`}
                      sizes={`(max-width: 768px) 50vw, ${100 / columns}vw`}
                      alt={img.altText || p.node.title}
                      loading={eager ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={eager ? "high" : "auto"}
                      className="w-full h-full object-cover grayscale opacity-60 sold-out-image"
                    />
                  ) : (
                    <div className="w-full h-full bg-secondary" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="sold-out-stamp bg-foreground text-background font-display text-sm md:text-base tracking-widest px-4 py-2 shadow-lg border-2 border-background/20">
                      SOLD OUT
                    </span>
                  </div>
                </div>
                <h3 className="font-display text-base md:text-lg leading-snug text-muted-foreground line-clamp-2 min-h-[3rem]">{p.node.title}</h3>
                <p className="font-display text-base md:text-lg text-muted-foreground mt-1.5 tabular-nums line-through">
                  {formatConvertedPrice(price.amount, price.currencyCode, displayCurrency)}
                </p>
              </div>
            ) : (
            <Link to={`/product/${p.node.handle}`} className="flex flex-col flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <div className="relative bg-muted aspect-square overflow-hidden mb-4">
                <span className="absolute top-4 left-4 font-display text-xs text-muted-foreground z-10 transition-opacity duration-300 group-hover:opacity-0">BRIM MOB</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nowFav = toggleFavorite(p.node.id);
                    toast.success(nowFav ? "Added to favourites" : "Removed from favourites", { position: "top-center" });
                  }}
                  aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
                  aria-pressed={isFav}
                  className={`absolute top-3 right-3 z-10 w-10 h-10 bg-background/85 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:bg-background active:scale-90 ${
                    isFav
                      ? "opacity-100 translate-y-0"
                      : "opacity-100 lg:opacity-0 lg:-translate-y-1 lg:group-hover:opacity-100 lg:group-hover:translate-y-0"
                  }`}
                >
                  <Heart className={`w-4 h-4 transition-colors ${isFav ? "fill-accent text-accent" : ""}`} />
                </button>
                {img ? (
                  <img
                    src={shopifyImage(img.url, 600)}
                    srcSet={`${shopifyImage(img.url, 400)} 400w, ${shopifyImage(img.url, 600)} 600w, ${shopifyImage(img.url, 900)} 900w`}
                    sizes={`(max-width: 768px) 50vw, ${100 / columns}vw`}
                    alt={img.altText || p.node.title}
                    loading={eager ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={eager ? "high" : "auto"}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                ) : (
                  <div className="w-full h-full bg-secondary" />
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <h3 className="font-display text-base md:text-lg leading-snug transition-colors duration-300 group-hover:text-accent line-clamp-2 min-h-[3rem]">{p.node.title}</h3>
              <p className="font-display text-base md:text-lg text-accent mt-1.5 tabular-nums">
                {formatConvertedPrice(price.amount, price.currencyCode, displayCurrency)}
              </p>
            </Link>
            )}
            {soldOut ? (
              <NotifyMeDialog productId={p.node.id} productHandle={p.node.handle} productTitle={p.node.title} />
            ) : (
              <>
              {showSizes && sizeVariants.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {sizeVariants.map(v => {
                    const sizeVal = v.node.selectedOptions.find(o => o.name.toLowerCase() === "size")?.value || v.node.title;
                    const isSel = selectedVariantId === v.node.id;
                    const disabled = !v.node.availableForSale;
                    return (
                      <button
                        key={v.node.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!disabled) setSelectedVariants(prev => ({ ...prev, [p.node.id]: v.node.id }));
                        }}
                        disabled={disabled}
                        aria-pressed={isSel}
                        className={`px-2.5 py-1 text-xs font-display border transition-colors ${
                          isSel
                            ? "bg-foreground text-background border-foreground"
                            : "border-border hover:border-foreground"
                        } ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                      >
                        {sizeVal}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={() => handleAdd(p)}
                disabled={isLoading}
                className="btn-shine mt-3 w-full font-display text-sm tracking-wider py-3.5 transition-all duration-300 disabled:opacity-50 bg-foreground text-background hover:bg-accent hover:text-accent-foreground active:scale-[0.98] touch-manipulation"
              >
                {isLoading ? "ADDING..." : "ADD TO CART"}
              </button>
              </>
            )}
          </article>
        );
  };

  if (layout === "carousel") {
    const basisClass =
      columns === 5
        ? "basis-[70%] sm:basis-[40%] md:basis-[30%] lg:basis-[18%]"
        : "basis-[70%] sm:basis-[40%] md:basis-[30%] lg:basis-[23%]";
    return (
      <div className="relative group/carousel">
        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {products.map((p, i) => (
            <div
              key={p.node.id}
              className={`snap-start shrink-0 ${basisClass}`}
            >
              {renderCard(p, i)}
            </div>
          ))}
        </div>
        <button
          type="button"
          aria-label="Previous"
          onClick={() => scrollByDir(-1)}
          disabled={!canLeft}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-background text-foreground items-center justify-center shadow-lg transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={() => scrollByDir(1)}
          disabled={!canRight}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-12 h-12 rounded-full bg-background text-foreground items-center justify-center shadow-lg transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }

  const gridColsClass = columns === 5 ? "grid-cols-2 lg:grid-cols-5" : "grid-cols-2 lg:grid-cols-4";

  return (
    <>
      <div className={`grid ${gridColsClass} gap-6`}>
        {(paginate ? products.slice((page - 1) * pageSize, page * pageSize) : products).map((p, i) => renderCard(p, i))}
      </div>
      {paginate && products.length > pageSize && (
        <div className="flex items-center justify-center gap-2 mt-10 font-display">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-2 border border-border hover:border-foreground disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          {Array.from({ length: Math.ceil(products.length / pageSize) }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} className={`w-10 h-10 border ${page === i + 1 ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>{i + 1}</button>
          ))}
          <button onClick={() => setPage((p) => Math.min(Math.ceil(products.length / pageSize), p + 1))} disabled={page >= Math.ceil(products.length / pageSize)} className="px-3 py-2 border border-border hover:border-foreground disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </>
  );
};